import { useMemo, useState } from "react";
import { countryName, fmtDuration, sessionBadges, sessionPath, sessionSource, type Session } from "../../web-analytics";

const PHONE_ROWS = 10;

const BADGE_CLASS = { money: "success", engaged: "info", bounce: "", click: "wa-badge-click" } as const;

/**
 * The latest sessions, newest first. Ported from Sonar's SessionsPanel. Below
 * 760px each row becomes a labelled card (the DataTable `data-label` pattern),
 * with the path as the card's title.
 */
export default function SessionsPanel({ sessions, timeZone, error }: { sessions: Session[] | null; timeZone: string; error?: string | null }) {
  // Fifty cards is a very long phone page, so phones show the first ten until asked (CSS hides the rest).
  const [all, setAll] = useState(false);
  const started = useMemo(
    () => new Intl.DateTimeFormat("en-US", { timeZone, month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }),
    [timeZone],
  );
  return (
    <section className="panel wa-sessions" aria-labelledby="wa-sessions-heading">
      <div className="panel-heading">
        <div className="wa-bd-title">
          <h2 id="wa-sessions-heading">Recent sessions</h2>
          {/* Filters pick WHICH visits appear; a kept visit is shown whole
              (nola_web_sessions), so under a page filter its pages and
              duration can be larger than the filtered headline. */}
          <p className="panel-meta">Latest 50 that match the filters. Each row describes the whole visit; narrow the range to see older ones.</p>
        </div>
      </div>
      {error ? (
        <div className="wa-bd-empty wa-bd-error" role="alert">{error}</div>
      ) : !sessions ? (
        <div className="wa-bd-empty">Loading sessions…</div>
      ) : sessions.length === 0 ? (
        <div className="wa-bd-empty">No sessions in this range</div>
      ) : (
        <div className="table-wrap wa-sess-wrap">
          <table className={all ? "wa-sess-table" : "wa-sess-table wa-sess-short"}>
            <thead>
              <tr>
                <th scope="col">Started</th>
                <th scope="col">Path</th>
                <th scope="col" className="num">Pages</th>
                <th scope="col" className="num">Duration</th>
                <th scope="col">Source</th>
                <th scope="col">Country</th>
                <th scope="col">Device</th>
                <th scope="col">Signals</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => {
                const path = sessionPath(s);
                const badges = sessionBadges(s);
                return (
                  <tr key={s.session_key}>
                    <td data-label="Started" className="wa-sess-started"><time dateTime={s.started_at}>{started.format(new Date(s.started_at))}</time></td>
                    <td data-label="Path" className="wa-sess-path" title={path}>{path}</td>
                    <td data-label="Pages" className="num">{s.pageviews}</td>
                    <td data-label="Duration" className="num">{fmtDuration(s.duration_seconds)}</td>
                    <td data-label="Source" className="wa-sess-clip" title={sessionSource(s)}>{sessionSource(s)}</td>
                    <td data-label="Country">{s.country ? countryName(s.country) : "—"}</td>
                    <td data-label="Device" title={[s.browser, s.os].filter(Boolean).join(" on ") || undefined}>{s.device || "—"}</td>
                    <td data-label="Signals">
                      {badges.length ? (
                        <span className="wa-badges">
                          {badges.map((b) => (
                            <span key={b.kind} className={`status-badge ${BADGE_CLASS[b.kind]}`} title={b.title}>{b.label}</span>
                          ))}
                        </span>
                      ) : <span className="wa-none">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!all && sessions.length > PHONE_ROWS && (
            <button type="button" className="wa-bd-more wa-sess-all" onClick={() => setAll(true)}>Show all {sessions.length} sessions</button>
          )}
        </div>
      )}
    </section>
  );
}
